import React, { useState, useEffect } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { notifyToast } from '../../lib/toastStore';
import Modal from '../shared/Modal';
import SearchableSelect from '../shared/SearchableSelect';
import { FinanceProduct, InvoiceLineItem } from '../../types';
import {
  X, Plus, Trash2, DollarSign, Calculator, Sparkles, Check, FileText
} from 'lucide-react';

interface InvoiceTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (templateData: any) => Promise<void>;
  existingTemplate?: any | null;
  products: FinanceProduct[];
  programs?: any[];
  sponsors?: any[];
}

export default function InvoiceTemplateModal({
  isOpen,
  onClose,
  onSave,
  existingTemplate,
  products,
}: InvoiceTemplateModalProps) {
  useBodyScrollLock(isOpen);

  const [name, setName] = useState(existingTemplate?.name || '');
  const [code, setCode] = useState(existingTemplate?.code || '');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
    if (existingTemplate && Array.isArray(existingTemplate.types) && existingTemplate.types.length > 0) {
      return existingTemplate.types;
    }
    if (existingTemplate?.type) {
      return [existingTemplate.type];
    }
    return ['invoice'];
  });
  const [description, setDescription] = useState(existingTemplate?.description || '');
  const [currency, setCurrency] = useState(existingTemplate?.currency || 'USD');
  const [active, setActive] = useState<boolean>(existingTemplate?.active !== false);
  const [notes, setNotes] = useState(existingTemplate?.notes || 'Thank you for choosing our services. Please make payments by the due date.');
  const [adjustment, setAdjustment] = useState<number>(existingTemplate?.adjustment || 0);
  const [adjustmentReason, setAdjustmentReason] = useState(existingTemplate?.adjustmentReason || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Line items
  const [items, setItems] = useState<InvoiceLineItem[]>(() => {
    if (existingTemplate && Array.isArray(existingTemplate.items) && existingTemplate.items.length > 0) {
      return existingTemplate.items;
    }
    return [
      {
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        discountType: 'fixed',
        discountValue: 0,
        discountAmount: 0,
        amount: 0,
        isCustom: true,
      },
    ];
  });

  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name || '');
      setCode(existingTemplate.code || '');
      if (Array.isArray(existingTemplate.types) && existingTemplate.types.length > 0) {
        setSelectedTypes(existingTemplate.types);
      } else if (existingTemplate.type) {
        setSelectedTypes([existingTemplate.type]);
      } else {
        setSelectedTypes(['invoice']);
      }
      setDescription(existingTemplate.description || '');
      setCurrency(existingTemplate.currency || 'USD');
      setActive(existingTemplate.active !== false);
      setNotes(existingTemplate.notes || 'Thank you for choosing our services.');
      setAdjustment(existingTemplate.adjustment || 0);
      setAdjustmentReason(existingTemplate.adjustmentReason || '');
      if (Array.isArray(existingTemplate.items) && existingTemplate.items.length > 0) {
        setItems(existingTemplate.items);
      }
    }
  }, [existingTemplate]);

  // Recalculate line item amounts
  const calculateItemAmount = (item: InvoiceLineItem): { amount: number; discountAmount: number } => {
    const rawTotal = (item.quantity || 1) * (item.rate || 0);
    let discountAmount = 0;

    if (item.discountValue && item.discountValue > 0) {
      if (item.discountType === 'percentage') {
        discountAmount = (rawTotal * item.discountValue) / 100;
      } else {
        discountAmount = item.discountValue;
      }
    }

    discountAmount = Math.min(rawTotal, discountAmount);
    const amount = Math.max(0, rawTotal - discountAmount);
    return { amount, discountAmount };
  };

  const handleAddItemFromCatalogue = (productId: string) => {
    if (!productId) return;
    const prod = products.find(p => p.id === productId);
    if (!prod) return;

    const newItem: InvoiceLineItem = {
      id: 'item_' + Math.random().toString(36).substring(2, 9),
      productId: prod.id,
      name: prod.name,
      description: prod.description || '',
      quantity: 1,
      rate: prod.sellingPrice || 0,
      discountType: 'fixed',
      discountValue: 0,
      discountAmount: 0,
      amount: prod.sellingPrice || 0,
      isCustom: false,
    };

    setItems(prev => [...prev.filter(i => i.name.trim() !== ''), newItem]);
  };

  const handleAddCustomItem = () => {
    setItems(prev => [
      ...prev,
      {
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        discountType: 'fixed',
        discountValue: 0,
        discountAmount: 0,
        amount: 0,
        isCustom: true,
      },
    ]);
  };

  const handleUpdateItem = (id: string, updates: Partial<InvoiceLineItem>) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, ...updates };
        const { amount, discountAmount } = calculateItemAmount(updated);
        return { ...updated, amount, discountAmount };
      })
    );
  };

  const handleRemoveItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  // Calculations
  const subtotal = items.reduce((sum, i) => sum + ((i.quantity || 1) * (i.rate || 0)), 0);
  const totalDiscount = items.reduce((sum, i) => sum + (i.discountAmount || 0), 0);
  const grandTotal = Math.max(0, subtotal - totalDiscount + (adjustment || 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      notifyToast('warning', 'Template Name is required.');
      return;
    }
    if (code.trim() && !/^\d{6}$/.test(code.trim())) {
      notifyToast('warning', 'Template Code must be a 6-digit number (e.g. 193837). Leave blank to auto-generate.');
      return;
    }
    if (selectedTypes.length === 0) {
      notifyToast('warning', 'Please select at least one Template Type.');
      return;
    }

    const validItems = items.filter(i => i.name.trim() !== '');
    if (validItems.length === 0) {
      notifyToast('warning', 'Please add at least one valid line item with a name.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        id: existingTemplate?.id,
        name: name.trim(),
        code: code.trim() || undefined,
        type: selectedTypes[0] || 'invoice',
        types: selectedTypes,
        description: description.trim(),
        currency,
        active,
        items: validItems,
        subtotal,
        totalDiscount,
        adjustment,
        adjustmentReason,
        total: grandTotal,
        notes: notes.trim(),
      });
      onClose();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save invoice template.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden text-xs animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/80 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-100 text-brand rounded-xl">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-slate-900 font-display">
                {existingTemplate ? `Edit Template: ${existingTemplate.name}` : 'Create Reusable Invoice Template'}
              </h2>
              <p className="text-[11px] text-slate-500">Configure pre-declared product line items, rates, discounts, and payment terms.</p>
            </div>
          </div>

          <button aria-label="Close" title="Close"
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Template Details Block */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
            <div className="sm:col-span-2 space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Template Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Standard J-1 Exchange Program Package"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none focus:border-brand"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">6-Digit Code (Auto-generated if blank)</label>
              <input
                type="text"
                maxLength={6}
                placeholder="Auto e.g. 193837"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none focus:border-brand"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Template Type(s) *</label>
              <select
                multiple
                value={selectedTypes}
                onChange={e => {
                  const opts = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedTypes(opts.length > 0 ? opts : ['invoice']);
                }}
                className="w-full bg-white border border-slate-200 rounded-lg p-1.5 font-semibold text-slate-800 outline-none focus:border-brand h-16"
              >
                <option value="invoice">Invoice Template</option>
                <option value="quote">Quote Template</option>
              </select>
              <p className="text-[11px] text-slate-500">Hold Ctrl/Cmd to select multiple</p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Currency</label>
              <select
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:border-brand"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Status</label>
              <select
                value={active ? 'active' : 'inactive'}
                onChange={e => setActive(e.target.value === 'active')}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:border-brand"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="sm:col-span-3 space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Template Description</label>
              <input
                type="text"
                placeholder="e.g. Includes J-1 visa application, health insurance, and administrative fees."
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-800 outline-none focus:border-brand"
              />
            </div>
          </div>

          {/* Line Items Builder Section */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h3 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="h-4 w-4 text-brand" />
                Line Items & Declared Products
              </h3>

              <div className="flex gap-2 w-full sm:w-auto">
                <div className="w-64">
                  <SearchableSelect
                    value=""
                    onChange={v => { if (v) handleAddItemFromCatalogue(v); }}
                    options={products.map(p => ({
                      value: p.id,
                      label: `${p.name} ($${(p.sellingPrice || 0).toFixed(2)})`,
                    }))}
                    placeholder="+ Add Product from Catalogue..."
                    allowClear={false}
                    emptyLabel="No products found"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleAddCustomItem}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-all cursor-pointer flex items-center gap-1 shrink-0"
                >
                  <Plus className="h-3.5 w-3.5" /> Custom Item
                </button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="p-3 w-1/3">Item / Product Name</th>
                    <th className="p-3">Details / Notes</th>
                    <th className="p-3 w-16 text-center">Qty</th>
                    <th className="p-3 w-24 text-right">Rate ($)</th>
                    <th className="p-3 w-28 text-right">Discount</th>
                    <th className="p-3 w-28 text-right">Amount ($)</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50">
                      <td className="p-3 align-top">
                        <input
                          type="text"
                          placeholder="e.g. Program Fee"
                          value={item.name}
                          onChange={e => handleUpdateItem(item.id, { name: e.target.value, isCustom: !item.productId })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 font-bold text-slate-800 outline-none focus:border-brand"
                        />
                      </td>

                      <td className="p-3 align-top">
                        <textarea
                          rows={2}
                          placeholder="Description / details..."
                          value={item.description}
                          onChange={e => handleUpdateItem(item.id, { description: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-slate-600 outline-none focus:border-brand resize-none"
                        />
                      </td>

                      <td className="p-3 align-top text-center">
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => handleUpdateItem(item.id, { quantity: Number(e.target.value) })}
                          className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 text-center font-bold text-slate-800 outline-none focus:border-brand"
                        />
                      </td>

                      <td className="p-3 align-top text-right">
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.rate}
                          onChange={e => handleUpdateItem(item.id, { rate: Number(e.target.value) })}
                          className="w-20 bg-white border border-slate-200 rounded-lg p-1.5 text-right font-bold text-slate-800 outline-none focus:border-brand font-mono"
                        />
                      </td>

                      <td className="p-3 align-top">
                        <div className="flex items-center gap-1 justify-end">
                          <select
                            value={item.discountType || 'fixed'}
                            onChange={e => handleUpdateItem(item.id, { discountType: e.target.value as any })}
                            className="bg-slate-50 border border-slate-200 rounded-lg p-1 text-[10px] font-bold"
                          >
                            <option value="fixed">$</option>
                            <option value="percentage">%</option>
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            value={item.discountValue || 0}
                            onChange={e => handleUpdateItem(item.id, { discountValue: Number(e.target.value) })}
                            className="w-16 bg-white border border-slate-200 rounded-lg p-1 text-right font-bold text-slate-800 outline-none focus:border-brand font-mono"
                          />
                        </div>
                        {item.discountAmount > 0 && (
                          <div className="text-[11px] text-emerald-600 text-right font-bold mt-1">
                            -${item.discountAmount.toFixed(2)}
                          </div>
                        )}
                      </td>

                      <td className="p-3 align-top text-right font-bold font-mono text-slate-900 pt-3">
                        ${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      <td className="p-3 align-top text-center pt-2">
                        <button aria-label="Remove item" title="Remove item"
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={items.length <= 1}
                          className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 cursor-pointer transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Calculations Summary & Manual Adjustment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Manual Dollar Adjustment (+ / -)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={adjustment}
                    onChange={e => setAdjustment(Number(e.target.value))}
                    className="w-32 bg-white border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-800 outline-none focus:border-brand"
                  />
                  <input
                    type="text"
                    placeholder="Reason for adjustment"
                    value={adjustmentReason}
                    onChange={e => setAdjustmentReason(e.target.value)}
                    className="flex-1 bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-700 outline-none focus:border-brand"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Default Terms & Notes</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-700 outline-none focus:border-brand resize-none"
                />
              </div>
            </div>

            {/* Subtotal & Totals Box */}
            <div className="space-y-2 bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-center">
              <div className="flex justify-between items-center text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono font-bold">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between items-center text-emerald-600">
                  <span>Line Item Discounts:</span>
                  <span className="font-mono font-bold">-${totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {adjustment !== 0 && (
                <div className="flex justify-between items-center text-indigo-600">
                  <span>Manual Adjustment:</span>
                  <span className="font-mono font-bold">{adjustment > 0 ? '+' : ''}${adjustment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <hr className="border-slate-100 my-1" />
              <div className="flex justify-between items-center text-slate-900 font-extrabold text-sm">
                <span>Template Total:</span>
                <span className="font-mono text-base text-blue-600">${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-xl font-bold transition-all cursor-pointer"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-5 py-2 btn-primary text-white font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 shadow-md shadow-blue-500/20"
          >
            {isSubmitting ? 'Saving Template...' : 'Save Invoice Template'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
